import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface PostRoom {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  cover_image: string | null;
  sort_order: number;
  created_at: string;
}

export interface Post {
  id: string;
  user_id: string;
  caption: string | null;
  image_url: string;
  likes_count: number;
  comments_count: number;
  created_at: string;
  updated_at: string;
  room_id: string | null;
  shared_article_id: string | null;
  shared_product_id: string | null;
  profile?: {
    username: string | null;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
  liked_by_me?: boolean;
  shared_article?: {
    id: string;
    title: string;
    description: string;
    image_key: string;
    slug: string;
  } | null;
  shared_product?: {
    id: string;
    name: string;
    description: string | null;
    cover_image: string | null;
    base_price: number;
    slug: string;
  } | null;
}

export interface PostComment {
  id: string;
  post_id: string;
  user_id: string;
  content: string;
  created_at: string;
  profile?: {
    username: string | null;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
}

export function usePosts(roomId?: string | null) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [rooms, setRooms] = useState<PostRoom[]>([]);

  const fetchRooms = useCallback(async () => {
    const { data } = await supabase
      .from("post_rooms")
      .select("*")
      .order("sort_order", { ascending: true });
    setRooms(data || []);
  }, []);

  const fetchPosts = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    
    setCurrentUserId(session.user.id);

    let query = supabase
      .from("posts")
      .select("*")
      .order("created_at", { ascending: false });

    if (roomId) {
      query = query.eq("room_id", roomId);
    }

    const { data: postsData, error } = await query;

    if (error) {
      console.error("Error fetching posts:", error);
      return;
    }

    if (!postsData || postsData.length === 0) {
      setPosts([]);
      setLoading(false);
      return;
    }

    // Fetch profiles and likes
    const userIds = [...new Set(postsData.map(p => p.user_id))];
    const { data: profiles } = await supabase
      .from("profiles")
      .select("user_id, username, first_name, last_name, avatar_url")
      .in("user_id", userIds);

    const { data: myLikes } = await supabase
      .from("post_likes")
      .select("post_id")
      .eq("user_id", session.user.id);

    // Fetch shared articles and products
    const articleIds = postsData.filter(p => p.shared_article_id).map(p => p.shared_article_id!);
    const productIds = postsData.filter(p => p.shared_product_id).map(p => p.shared_product_id!);

    let articlesMap = new Map();
    let productsMap = new Map();

    if (articleIds.length > 0) {
      const { data: articles } = await supabase
        .from("articles")
        .select("id, title, description, image_key, slug")
        .in("id", articleIds);
      articlesMap = new Map(articles?.map(a => [a.id, a]) || []);
    }

    if (productIds.length > 0) {
      const { data: products } = await supabase
        .from("products")
        .select("id, name, description, cover_image, base_price, slug")
        .in("id", productIds);
      productsMap = new Map(products?.map(p => [p.id, p]) || []);
    }

    const likedPostIds = new Set(myLikes?.map(l => l.post_id) || []);
    const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);

    const enrichedPosts: Post[] = postsData.map(post => ({
      ...post,
      profile: profileMap.get(post.user_id) || undefined,
      liked_by_me: likedPostIds.has(post.id),
      shared_article: post.shared_article_id ? articlesMap.get(post.shared_article_id) || null : null,
      shared_product: post.shared_product_id ? productsMap.get(post.shared_product_id) || null : null,
    }));

    setPosts(enrichedPosts);
    setLoading(false);
  }, [roomId]);

  useEffect(() => {
    fetchPosts();
    fetchRooms();
  }, [fetchPosts, fetchRooms]);

  const createPost = async (
    imageFile: File | null,
    caption: string,
    options?: { roomId?: string | null; sharedArticleId?: string | null; sharedProductId?: string | null }
  ) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Non autenticato");

    const { data: profile } = await supabase
      .from("profiles")
      .select("username")
      .eq("user_id", session.user.id)
      .maybeSingle();

    if (!profile?.username) {
      throw new Error("USERNAME_REQUIRED");
    }

    let publicUrl = "";

    if (imageFile) {
      const fileExt = imageFile.name.split(".").pop();
      const filePath = `${session.user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("post-images")
        .upload(filePath, imageFile, { upsert: true });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from("post-images")
        .getPublicUrl(filePath);
      publicUrl = data.publicUrl;
    }

    // If sharing article/product without image, use a placeholder
    if (!publicUrl && (options?.sharedArticleId || options?.sharedProductId)) {
      publicUrl = "shared-content";
    }

    if (!publicUrl) throw new Error("Immagine richiesta");

    const { error: insertError } = await supabase
      .from("posts")
      .insert({
        user_id: session.user.id,
        image_url: publicUrl,
        caption: caption.trim() || null,
        room_id: options?.roomId || null,
        shared_article_id: options?.sharedArticleId || null,
        shared_product_id: options?.sharedProductId || null,
      });

    if (insertError) throw insertError;

    await fetchPosts();
  };

  const toggleLike = async (postId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const post = posts.find(p => p.id === postId);
    if (!post) return;

    if (post.liked_by_me) {
      await supabase.from("post_likes").delete().eq("post_id", postId).eq("user_id", session.user.id);
    } else {
      await supabase.from("post_likes").insert({ post_id: postId, user_id: session.user.id });
    }

    setPosts(prev => prev.map(p => 
      p.id === postId 
        ? { ...p, liked_by_me: !p.liked_by_me, likes_count: p.liked_by_me ? p.likes_count - 1 : p.likes_count + 1 }
        : p
    ));
  };

  const deletePost = async (postId: string) => {
    const { error } = await supabase.from("posts").delete().eq("id", postId);
    if (error) { toast.error("Errore durante l'eliminazione"); return; }
    setPosts(prev => prev.filter(p => p.id !== postId));
    toast.success("Post eliminato");
  };

  const fetchComments = async (postId: string): Promise<PostComment[]> => {
    const { data: comments, error } = await supabase
      .from("post_comments")
      .select("*")
      .eq("post_id", postId)
      .order("created_at", { ascending: true });

    if (error || !comments) return [];

    const userIds = [...new Set(comments.map(c => c.user_id))];
    const { data: profiles } = await supabase
      .from("profiles")
      .select("user_id, username, first_name, last_name, avatar_url")
      .in("user_id", userIds);

    const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);
    return comments.map(c => ({ ...c, profile: profileMap.get(c.user_id) || undefined }));
  };

  const addComment = async (postId: string, content: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { error } = await supabase
      .from("post_comments")
      .insert({ post_id: postId, user_id: session.user.id, content: content.trim() });

    if (error) { toast.error("Errore nell'invio del commento"); return; }

    setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, comments_count: p.comments_count + 1 } : p
    ));
  };

  const deleteComment = async (commentId: string, postId: string) => {
    const { error } = await supabase.from("post_comments").delete().eq("id", commentId);
    if (error) { toast.error("Errore nell'eliminazione del commento"); return; }
    setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, comments_count: Math.max(p.comments_count - 1, 0) } : p
    ));
  };

  // Room management (admin)
  const createRoom = async (name: string, description?: string) => {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const { error } = await supabase.from("post_rooms").insert({ name, slug, description: description || null });
    if (error) { toast.error("Errore nella creazione della stanza"); return; }
    toast.success("Stanza creata");
    await fetchRooms();
  };

  const deleteRoom = async (roomId: string) => {
    const { error } = await supabase.from("post_rooms").delete().eq("id", roomId);
    if (error) { toast.error("Errore nell'eliminazione della stanza"); return; }
    toast.success("Stanza eliminata");
    await fetchRooms();
  };

  return {
    posts,
    loading,
    currentUserId,
    rooms,
    createPost,
    toggleLike,
    deletePost,
    fetchComments,
    addComment,
    deleteComment,
    refreshPosts: fetchPosts,
    createRoom,
    deleteRoom,
  };
}
